/** @jsx jsx */
/** @jsxFrag React.Fragment */
import IconClipboard from '@apollo/icons/default/IconClipboard.svg';
import IconConnectors from '@apollo/icons/default/IconConnectors.svg';
import IconHistory from '@apollo/icons/default/IconHistory.svg';
import IconInfo from '@apollo/icons/default/IconInfo.svg';
import IconTeam from '@apollo/icons/default/IconTeam.svg';
import {
  Alert,
  Card,
  CardBody,
  CardDescription,
  CardHeader,
  CardHeaderControl,
  CardTitle,
  Group,
  IconButton,
  Link,
  Text,
  customColors,
} from '@apollo/orbit';
import { css, jsx } from '@emotion/react';
import classnames from 'classnames';
import copy from 'copy-to-clipboard';
import { DocumentNode } from 'graphql';
import * as monacoEditor from 'monaco-editor';
import React from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { getRangesToRemove } from 'src/app/graph/documentationPage/schemaPage/sdlFilterHelpers';
import { getGraphMetaData } from 'src/app/graph/explorerPage/helpers/federationHelpers';
import { useInternalGraphLinking } from 'src/app/graph/hooks/useInternalGraphLinking';
import { MonacoFile } from 'src/app/graph/shared/monacoHelpers/monacoHelpers';
import { Tooltip } from 'src/components/common/tooltip/Tooltip';
import { LocalTimestamp } from 'src/components/timestamp/Timestamp';
import { useTabbedStudioEditor } from 'src/hooks/useTabbedStudioEditor';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';
import { safeCompileMarkdown } from 'src/lib/safeCompileMarkdown';

import { SDLEditor, SDL_EDITOR_MONACO_OPTIONS } from './sdlEditor/SDLEditor';
import { SDL_EDITOR_ID } from './sdlEditorId';
import { uriToServiceName } from './uriToServiceName';

export const API_SCHEMA_ID = '#@!api!@#';
export const SUPERGRAPH_SCHEMA_ID = '#@!supergraph!@#';
export const SANDBOX_SCHEMA_ID = '#@!api!@#';

export interface SchemaInfo extends MonacoFile {
  type: 'Subgraph' | 'Graph';
  displayName: string;
  url?: string | undefined;
  version: string | undefined;
  lastUpdated: Date | undefined;
  typeDefs: DocumentNode;
  typeCount: number;
  fieldCount: number;
  isLive?: boolean;
  isConnector?: boolean;
}

function getSDLCodeEditorOpener(
  openFileRef: React.MutableRefObject<
    (serviceName: string, range?: monacoEditor.IRange | undefined) => void
  >,
): monacoEditor.editor.ICodeEditorOpener {
  return {
    openCodeEditor: (_, resource, selection) => {
      openFileRef.current(
        uriToServiceName(resource),
        selection && 'startLineNumber' in selection ? selection : undefined,
      );
      return true;
    },
  };
}

export const SDLDisplay = ({
  className,
  selectedSchemaInfo,
  selectedSchemaName,
  schemaInfoMap,
  showComments,
  showDeprecated,
  buildErrors,
  editorActions,
}: {
  className?: string;
  selectedSchemaInfo: SchemaInfo;
  selectedSchemaName: string;
  schemaInfoMap: Record<string, SchemaInfo>;
  showComments: boolean;
  showDeprecated: boolean;
  buildErrors: GraphQLTypes.NewSdlPageQuery_service_variant_latestLaunch_build_result_BuildFailure_errorMessages[];
  editorActions: React.ReactNode;
  isContract?: boolean;
}) => {
  const {
    displayName,
    lastUpdated,
    version,
    typeCount,
    fieldCount,
    typeDefs,
    id: name,
    isLive = true,
    url,
    isConnector,
  } = selectedSchemaInfo;

  const history = useHistory();
  const location = useLocation();
  const { locationToSDL, sdlFlags } = useInternalGraphLinking();

  const setSelectedSchemaName = React.useCallback(
    (schemaName: string | undefined) => {
      history.push(
        locationToSDL({
          selectedSchema: schemaName === API_SCHEMA_ID ? undefined : schemaName,
        }),
      );
    },
    [locationToSDL, history],
  );

  const { editor, modelsByName } = useTabbedStudioEditor({
    controlContents: true,
    editorId: SDL_EDITOR_ID,
    files: schemaInfoMap,
    selectedFile: selectedSchemaName,
    setSelectedFile: setSelectedSchemaName,
    getCodeEditorOpener: getSDLCodeEditorOpener,
    maintainScrollPosition: true,
    ...SDL_EDITOR_MONACO_OPTIONS,
  });

  const [shouldPromptCopyURLSuccess, setShouldPromptCopyURLSuccess] =
    React.useState(false);

  React.useEffect(() => {
    if (shouldPromptCopyURLSuccess) {
      const timeout = setTimeout(() => {
        setShouldPromptCopyURLSuccess?.(false);
      }, 1000);
      return () => clearTimeout(timeout);
    }
  }, [setShouldPromptCopyURLSuccess, shouldPromptCopyURLSuccess]);

  const graphMetaData = getGraphMetaData(typeDefs);

  return (
    <div className={classnames(className, 'flex flex-col overflow-y-auto')}>
      {buildErrors?.length > 0 ? (
        name === API_SCHEMA_ID || name === SUPERGRAPH_SCHEMA_ID ? (
          <Alert status="warning" className=" mb-2">
            {`This version of the ${
              name === API_SCHEMA_ID ? 'API' : 'Supergraph'
            } schema does not include the latest from some
            of your subgraphs because the supergraph schema build failed.`}{' '}
            <Link
              to={setOverlayVisibleLocation({
                location,
                overlayName: Config.modals.buildErrors,
              })}
            >
              See details
            </Link>
          </Alert>
        ) : (
          !isLive && (
            <Alert status="error" className=" mb-2">
              This version of this subgraph is not included in the supergraph
              schema because the supergraph schema build failed.{' '}
              <Link
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.buildErrors,
                })}
              >
                See details
              </Link>
            </Alert>
          )
        )
      ) : null}

      <Card isDivided className="h-full">
        <CardHeader>
          <CardTitle>{displayName}</CardTitle>
          {isConnector ? (
            <CardDescription className="flex items-center">
              <IconConnectors className="mr-1 text-icon-primary" />
              <span>This is a server-free subgraph</span>
            </CardDescription>
          ) : !sdlFlags.omitUrl ? (
            <CardDescription className="flex items-center">
              <Tooltip
                disabled={!url}
                label={shouldPromptCopyURLSuccess ? 'Copied' : 'Copy URL'}
              >
                <IconButton
                  aria-label="Copy URL"
                  className="-ml-2"
                  size="sm"
                  variant="hidden"
                  isDisabled={!url}
                  icon={
                    <IconClipboard
                      onClick={() => {
                        setShouldPromptCopyURLSuccess(true);
                        copy(url || '');
                      }}
                    />
                  }
                />
              </Tooltip>
              <span className="text-secondary">{url ?? 'No url set'}</span>
            </CardDescription>
          ) : null}
          <CardDescription className="flex items-center">
            {(lastUpdated || version) && (
              <>
                <IconHistory className="mr-2 text-icon-primary" />
                {lastUpdated && <LocalTimestamp value={lastUpdated} />}
                {version && <span className="mx-1">•</span>}
              </>
            )}
            <Tooltip label={version}>
              <Text as="span" variant="code">
                {version?.slice(0, 6)}
              </Text>
            </Tooltip>
          </CardDescription>
          {graphMetaData.name.length > 0 && (
            <CardDescription className="flex items-center">
              <IconTeam className="mr-2 size-4 text-icon-primary" />
              {graphMetaData.url ? (
                <Link isExternal to={graphMetaData.url}>
                  {graphMetaData.name}
                </Link>
              ) : (
                graphMetaData.name
              )}
            </CardDescription>
          )}
          {graphMetaData.description &&
            graphMetaData.description.length > 0 && (
              <CardDescription
                className="flex items-center"
                css={css`
                  a {
                    color: ${customColors.text.link};
                    text-decoration: underline;
                    font-weight: 600;
                  }
                `}
              >
                <IconInfo className="mr-2 text-icon-primary" />
                {safeCompileMarkdown(graphMetaData.description)}
              </CardDescription>
            )}

          <CardHeaderControl>
            <Group gap="px" orientation="vertical">
              <Text as="span" variant="code" size="2xl">
                {typeCount}
              </Text>
              <Text as="span" size="sm" className="text-center text-secondary">
                types
              </Text>
            </Group>
            <Group gap="px" orientation="vertical">
              <Text as="span" variant="code" size="2xl">
                {fieldCount}
              </Text>
              <Text as="span" size="sm" className="text-center text-secondary">
                fields
              </Text>
            </Group>
          </CardHeaderControl>
        </CardHeader>
        <CardBody className="relative h-0 flex-1 p-0 last:pb-0">
          {editorActions}
          <SDLEditor
            className="size-full"
            editor={editor}
            modelsByName={modelsByName}
            schemaName={selectedSchemaName}
            documentNode={typeDefs}
            documentNodesByName={Object.fromEntries(
              Object.entries(schemaInfoMap).map(([key, value]) => [
                key,
                value.typeDefs,
              ]),
            )}
            rangesToHide={getRangesToRemove({
              showComments,
              showDeprecated,
              typeDefs,
              // TODO: Remove non null assertion, added when turning on noUncheckedIndexedAccess
              // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
              text: schemaInfoMap[selectedSchemaInfo.id]!.text,
            })}
          />
        </CardBody>
      </Card>
    </div>
  );
};
